// AgencyDashboard v2.3 - normalize() fully aligned with App.js field names
import { useState, useEffect, useMemo } from "react";
import { db, storage } from "./firebase";
import { collection, onSnapshot, doc, updateDoc, deleteDoc, orderBy, query, where } from "firebase/firestore";
import { ref, getDownloadURL } from "firebase/storage";
import ReferencesPanel from "./ReferencesPanel";
import DBSPanel from "./DBSPanel";

// Opens a Firebase Storage file in a new tab with a fresh signed URL
async function openDoc(storedUrl, label) {
  if (!storedUrl) return;
  try {
    // Extract the storage path from the stored URL
    const pathMatch = storedUrl.match(/\/o\/(.+?)\?/);
    if (!pathMatch) { window.open(storedUrl, "_blank"); return; }
    const storagePath = decodeURIComponent(pathMatch[1]);
    const freshUrl = await getDownloadURL(ref(storage, storagePath));
    window.open(freshUrl, "_blank");
  } catch (e) {
    console.error("Failed to open doc:", e);
    alert("Could not open " + (label || "document") + ". Please try again.");
  }
}

function normalize(d) {
  if (d.firstName) return d; // old flat format — pass through unchanged
  // App.js saves: p1(personal) p2(equal ops) p3(education) p4[](employment)
  //               p5(experience) p6(health/avail) p7(RTW+docs) p8(DBS)
  //               p9[](references) p10(bank) p11(declaration) urls(file URLs)
  const p1  = d.p1  || {};
  const p2  = d.p2  || {};
  const p3  = d.p3  || {};
  const p4  = Array.isArray(d.p4) ? d.p4 : [];
  const p5  = d.p5  || {};
  const p6  = d.p6  || {};
  const p7  = d.p7  || {};
  const p8  = d.p8  || {};
  const p9  = Array.isArray(d.p9) ? d.p9 : [];
  const p10 = d.p10 || {};
  const p11 = d.p11 || {};
  const urls = d.urls || {};
  return {
    ...d,
    // ── Step 1: Personal Details ──────────────────────────────
    firstName:         p1.firstName    || "",
    lastName:          p1.lastName     || "",
    middleName:        p1.middleName   || "",
    dob:               p1.dob          || "",
    gender:            p1.gender       || "",
    nationality:       p1.nationality  || "",
    religion:          p1.religion     || "",
    email:             p1.email        || "",
    phone:             p1.phone        || "",
    address1:          p1.address1     || "",
    address2:          p1.address2     || "",
    city:              p1.city         || "",
    county:            p1.county       || "",
    postcode:          p1.postcode     || "",
    // Emergency contacts (App.js uses emergency1Name / emergency2Name)
    emergencyName:     p1.emergency1Name  || "",
    emergencyRelation: p1.emergency1Rel   || "",
    emergencyPhone:    p1.emergency1Phone || "",
    emergency2Name:    p1.emergency2Name  || "",
    emergency2Rel:     p1.emergency2Rel   || "",
    emergency2Phone:   p1.emergency2Phone || "",
    // ── Step 2: Equal Opportunities ──────────────────────────
    ethnicity:         p2.ethnicity        || "",
    disability:        p2.disability       || "",
    disabilityDetails: p2.disabilityDetails || "",
    // ── Step 3: Education & Training ─────────────────────────
    quals:             p3.qualifications || [],   // App.js: p3.qualifications
    courses:           p3.courses        || "",
    firstAidExpiry:    p3.firstAidExpiry || "",
    memberships:       p3.memberships    || "",
    // ── Step 4: Employment History ────────────────────────────
    employmentHistory: p4,  // array of {employer, jobTitle, from, to, leaving, duties, gaps}
    // ── Step 5: Experience & Care Standards ──────────────────
    settings:          p5.careSettings   || [],   // App.js: p5.careSettings
    clients:           p5.clientGroups   || [],   // App.js: p5.clientGroups
    experience:        p5.experience     || "",
    whyCare:           p5.whyCare        || "",
    strengths:         p5.strengths      || "",
    challenging:       p5.challenging    || "",
    safeguarding:      p5.safeguarding   || "",
    // ── Step 6: Health & Availability ────────────────────────
    healthConditions:  p6.healthConditions || "",
    adjustments:       p6.adjustments      || "",
    transport:         p6.transport        || "",
    availableStart:    p6.availableStart   || "",
    interviewAvail:    p6.interviewAvail   || "",
    // ── Step 7: Right to Work & Documents ────────────────────
    rightToWork:       p7.rtwStatus || "",        // App.js: p7.rtwStatus
    rtwStatus:         p7.rtwStatus || "",
    docs:              p7.rtwDocs   || [],         // App.js: p7.rtwDocs
    niNumber:          p7.niNumber  || "",         // App.js: p7.niNumber
    shareCode:         p7.shareCode || "",
    visaExpiry:        p7.visaExpiry || "",
    // ── Step 8: DBS & Criminal ───────────────────────────────
    hasDbs:            p8.dbsType            || "",
    dbsNumber:         p8.dbsNumber          || "",
    dbsDate:           p8.dbsDate            || "",
    updateService:     p8.dbsUpdateService   || "",  // App.js: p8.dbsUpdateService
    updateServiceNum:  p8.updateServiceNumber || "",
    conviction:        p8.convictions         || "",  // App.js: p8.convictions
    convictionDetails: p8.convictionDetails   || "",
    // ── Step 9: References ────────────────────────────────────
    // App.js saves: {name, position, org, relationship, email, phone, address}
    // PDF/modal uses: r.title (→ position) and r.relation (→ relationship)
    refs: p9.map(r => ({
      ...r,
      title:    r.title    || r.position     || "",
      relation: r.relation || r.relationship || "",
    })),
    // ── Step 10: Bank Details ─────────────────────────────────
    // App.js: p10.bankName = bank name, p10.accountName = account holder
    bankName:      p10.accountName || "",  // displayed as "Account Holder"
    bankInstitute: p10.bankName    || "",  // the actual bank/building society
    sortCode:      p10.sortCode    || "",
    accountNumber: p10.accountNumber || "",
    // ── Step 11: Declaration ──────────────────────────────────
    signature: p11.signature || "",
    signedAt:  p11.signDate  || "",        // App.js: p11.signDate
    // ── Document URLs ─────────────────────────────────────────
    // App.js uploads to: urls.cv, urls.passport, urls.rtw, urls.poa1, urls.poa2, urls.dbs
    cvURL:       urls.cv       || urls.cvURL       || "",
    passportURL: urls.passport || urls.passportURL || "",
    rtwDocURL:   urls.rtw      || urls.rtwDocURL   || urls.rtwDoc || "",
    poa1URL:     urls.poa1     || urls.poa1URL     || "",
    poa2URL:     urls.poa2     || urls.poa2URL     || "",
    dbsDocURL:   urls.dbs      || urls.dbsURL      || urls.dbsDocURL || "",
    poa1Type:    urls.poa1Type || "",
    poa2Type:    urls.poa2Type || "",
    // ── Applied date ──────────────────────────────────────────
    appliedAt: d.submittedAt?.toDate
      ? d.submittedAt.toDate().toLocaleDateString("en-GB")
      : (d.appliedAt || ""),
  };
}

const STATUS_COLORS = {
  pending:   { bg: "#f5f0ff", text: "#6C3FC5", border: "#c5b3e8" },
  submitted: { bg: "#f5f0ff", text: "#6C3FC5", border: "#c5b3e8" },
  approved:  { bg: "#e8f5eb", text: "#1a7a3a", border: "#a3d9b1" },
  rejected:  { bg: "#fff0f0", text: "#cc0000", border: "#ffb3b3" },
};
const STATUS_LABELS = {
  pending:   "⏳ Pending",
  submitted: "⏳ Pending",
  approved:  "✅ Approved",
  rejected:  "❌ Rejected",
};

const s = {
  app: { minHeight: "100vh", background: "#f8f5ff", color: "#1a1a2e", fontFamily: "'DM Sans', sans-serif" },
  header: { background: "#ffffff", borderBottom: "1px solid #e8e0f5", padding: "16px 24px", display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, zIndex: 100 },
  logo: { display: "flex", alignItems: "center", gap: 10 },
  logoIcon: { width: 36, height: 36, borderRadius: 10, background: "#6C3FC5", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, fontWeight: 700, color: "white", fontFamily: "serif" },
  logoText: { color: "#6C3FC5", fontSize: 18, fontFamily: "'DM Serif Display', serif" },
  headerRight: { display: "flex", alignItems: "center", gap: 12 },
  agencyBadge: { background: "#f0ebff", border: "1px solid #c5b3e8", borderRadius: 6, padding: "4px 10px", fontSize: 11, color: "#6C3FC5", fontWeight: 600 },
  logoutBtn: { background: "none", border: "1px solid #c5b3e8", borderRadius: 6, padding: "4px 12px", color: "#9b7fd4", fontSize: 12, cursor: "pointer" },
  trainingBtn: { background: "#6C3FC5", border: "none", borderRadius: 6, padding: "6px 14px", color: "white", fontSize: 12, fontWeight: 600, cursor: "pointer" },
  tabRow: { display: "flex", gap: 4, padding: "0 24px", borderBottom: "1px solid #e8e0f5", background: "#ffffff" },
  tab: (active) => ({ padding: "12px 18px", fontSize: 13, fontWeight: active ? 700 : 400, color: active ? "#6C3FC5" : "#9b7fd4", borderBottom: active ? "2px solid #6C3FC5" : "2px solid transparent", background: "none", border: "none", cursor: "pointer" }),
  container: { maxWidth: 1100, margin: "0 auto", padding: "24px 16px" },
  statsRow: { display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 24 },
  statCard: (color) => ({ background: "#ffffff", borderRadius: 12, padding: "16px 20px", borderLeft: `3px solid ${color}` }),
  statNum: (color) => ({ fontSize: 28, fontWeight: 700, color, fontFamily: "'DM Serif Display', serif" }),
  statLabel: { fontSize: 11, color: "#9b7fd4", textTransform: "uppercase", letterSpacing: "0.08em", marginTop: 2 },
  applyLink: { background: "#ffffff", border: "1px solid #e8e0f5", borderRadius: 12, padding: "16px 20px", marginBottom: 20, display: "flex", alignItems: "center", justifyContent: "space-between" },
  applyLinkText: { color: "#6C3FC5", fontSize: 14, fontWeight: 600 },
  copyBtn: { padding: "8px 16px", background: "#6C3FC5", border: "none", borderRadius: 8, color: "white", fontSize: 13, cursor: "pointer" },
  toolbar: { display: "flex", gap: 10, marginBottom: 20, flexWrap: "wrap", alignItems: "center" },
  searchInput: { flex: 1, minWidth: 200, background: "#ffffff", border: "1px solid #e8e0f5", borderRadius: 8, padding: "10px 16px", color: "#1a1a2e", fontSize: 14, outline: "none" },
  filterBtn: (active) => ({ padding: "9px 14px", borderRadius: 8, border: `1px solid ${active ? "#6C3FC5" : "#e8e0f5"}`, background: active ? "#f0ebff" : "transparent", color: active ? "#6C3FC5" : "#9b7fd4", fontSize: 12, cursor: "pointer", fontWeight: active ? 600 : 400 }),
  exportBtn: { padding: "9px 16px", borderRadius: 8, border: "1px solid #e8e0f5", background: "transparent", color: "#9b7fd4", fontSize: 12, cursor: "pointer" },
  table: { width: "100%", borderCollapse: "collapse" },
  th: { textAlign: "left", padding: "10px 16px", fontSize: 11, color: "#9b7fd4", textTransform: "uppercase", letterSpacing: "0.08em", borderBottom: "1px solid #e8e0f5", fontWeight: 600 },
  td: { padding: "14px 16px", fontSize: 14, borderBottom: "1px solid #f0ebff", color: "#1a1a2e" },
  tdSub: { fontSize: 12, color: "#9b7fd4", marginTop: 2 },
  statusPill: (status) => ({ display: "inline-block", padding: "4px 10px", borderRadius: 999, fontSize: 11, fontWeight: 600, background: STATUS_COLORS[status]?.bg, color: STATUS_COLORS[status]?.text, border: `1px solid ${STATUS_COLORS[status]?.border}` }),
  modal: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 200, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "24px 16px", overflowY: "auto" },
  modalBox: { background: "#ffffff", border: "1px solid #e8e0f5", borderRadius: 16, width: "100%", maxWidth: 640, overflow: "hidden", marginBottom: 24 },
  modalHeader: { background: "#f8f5ff", padding: "20px 24px", borderBottom: "1px solid #e8e0f5", display: "flex", justifyContent: "space-between", alignItems: "flex-start" },
  modalTitle: { fontFamily: "'DM Serif Display', serif", fontSize: 22, color: "#1a1a2e" },
  closeBtn: { background: "none", border: "none", color: "#9b7fd4", fontSize: 22, cursor: "pointer" },
  modalBody: { padding: 24 },
  section: { marginBottom: 20 },
  sectionTitle: { fontSize: 11, color: "#6C3FC5", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 10, paddingBottom: 6, borderBottom: "1px solid #e8e0f5" },
  detailGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 },
  detailItem: { background: "#f8f5ff", borderRadius: 8, padding: "10px 14px" },
  detailLabel: { fontSize: 10, color: "#9b7fd4", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 3 },
  detailValue: { fontSize: 13, color: "#1a1a2e" },
  tagRow: { display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6 },
  tag: { padding: "4px 10px", borderRadius: 999, background: "#f0ebff", border: "1px solid #e8e0f5", fontSize: 12, color: "#6C3FC5" },
  actionRow: { display: "flex", gap: 10, padding: "16px 24px", borderTop: "1px solid #e8e0f5", background: "#f8f5ff" },
  approveBtn: { flex: 1, padding: "12px", borderRadius: 8, border: "none", background: "#6C3FC5", color: "white", fontSize: 14, fontWeight: 700, cursor: "pointer" },
  rejectBtn: { flex: 1, padding: "12px", borderRadius: 8, border: "1px solid #ffb3b3", background: "#fff0f0", color: "#cc0000", fontSize: 14, fontWeight: 600, cursor: "pointer" },
  pdfBtn: { padding: "12px 16px", borderRadius: 8, border: "1px solid #c5b3e8", background: "#f0ebff", color: "#6C3FC5", fontSize: 14, cursor: "pointer" },
  loading: { textAlign: "center", padding: "60px 20px", color: "#9b7fd4", fontSize: 14 },
  empty: { textAlign: "center", padding: "60px 20px", color: "#9b7fd4", fontSize: 14 },
};

function DetailItem({ label, value }) {
  return (
    <div style={s.detailItem}>
      <div style={s.detailLabel}>{label}</div>
      <div style={s.detailValue}>{value || "—"}</div>
    </div>
  );
}

function Modal({ app, agency, onClose, onApprove, onReject, onDelete }) {
  if (!app) return null;
  const downloadPDF = async () => {
    // Pre-fetch fresh signed URLs so PDF links don't 403
    const getFresh = async (storedUrl) => {
      if (!storedUrl) return null;
      try {
        const pathMatch = storedUrl.match(/\/o\/(.+?)\?/);
        if (!pathMatch) return storedUrl;
        const freshUrl = await getDownloadURL(ref(storage, decodeURIComponent(pathMatch[1])));
        return freshUrl;
      } catch { return null; }
    };
    const [freshCV, freshPassport, freshRTW, freshPoa1, freshPoa2, freshDBS] = await Promise.all([
      getFresh(app.cvURL), getFresh(app.passportURL), getFresh(app.rtwDocURL),
      getFresh(app.poa1URL), getFresh(app.poa2URL), getFresh(app.dbsDocURL),
    ]);

    const field = (label, value) => value ? `<div class="field"><div class="label">${label}</div><div class="value">${value}</div></div>` : '';
    const fieldFull = (label, value) => value ? `<div class="field full"><div class="label">${label}</div><div class="value">${value}</div></div>` : '';
    const tags = (arr) => arr?.length ? arr.map(t => `<span class="tag">${t}</span>`).join('') : null;
    const tagsFull = (label, arr) => arr?.length ? `<div class="field full"><div class="label">${label}</div><div class="tags">${tags(arr)}</div></div>` : '';
    const section = (title, fields) => { const content = fields.filter(Boolean).join(''); return content.trim() ? `<div class="section"><h2>${title}</h2><div class="grid">${content}</div></div>` : ''; };
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${app.firstName} ${app.lastName} — Application</title>
    <style>
      * { box-sizing: border-box; margin: 0; padding: 0; }
      body { font-family: Arial, sans-serif; color: #1a1a2e; font-size: 13px; line-height: 1.5; }
      .page { padding: 32px 40px; max-width: 900px; margin: 0 auto; }
      .header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #6C3FC5; padding-bottom: 16px; margin-bottom: 24px; }
      .header-left h1 { font-size: 24px; color: #6C3FC5; margin-bottom: 4px; }
      .header-left p { color: #9b7fd4; font-size: 12px; }
      .status { display: inline-block; padding: 4px 12px; border-radius: 999px; font-size: 11px; font-weight: 700; text-transform: uppercase; }
      .status-pending { background: #f5f0ff; color: #6C3FC5; border: 1px solid #c5b3e8; }
      .status-approved { background: #e8f5eb; color: #1a7a3a; border: 1px solid #a3d9b1; }
      .status-rejected { background: #fff0f0; color: #cc0000; border: 1px solid #ffb3b3; }
      .logo { font-size: 20px; font-weight: 700; color: #6C3FC5; }
      .section { margin-bottom: 20px; page-break-inside: avoid; }
      h2 { font-size: 11px; color: #6C3FC5; text-transform: uppercase; letter-spacing: 0.1em; border-bottom: 1px solid #e8e0f5; padding-bottom: 6px; margin-bottom: 10px; font-weight: 700; }
      .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
      .field { background: #f8f5ff; padding: 8px 12px; border-radius: 6px; }
      .field.full { grid-column: 1 / -1; }
      .label { font-size: 9px; color: #9b7fd4; text-transform: uppercase; letter-spacing: 0.08em; margin-bottom: 2px; }
      .value { font-size: 12px; color: #1a1a2e; }
      .tags { display: flex; flex-wrap: wrap; gap: 4px; margin-top: 4px; }
      .tag { background: #f0ebff; border: 1px solid #e8e0f5; border-radius: 999px; padding: 2px 8px; font-size: 11px; color: #6C3FC5; }
      .doc-row { display: flex; align-items: center; gap: 8px; padding: 6px 0; border-bottom: 1px solid #e8e0f5; font-size: 12px; }
      .doc-row:last-child { border-bottom: none; }
      .doc-link { color: #6C3FC5; font-size: 11px; }
      .footer { margin-top: 32px; padding-top: 12px; border-top: 1px solid #e8e0f5; display: flex; justify-content: space-between; font-size: 10px; color: #9b7fd4; }
      * { print-color-adjust: exact !important; -webkit-print-color-adjust: exact !important; color-adjust: exact !important; }
      @media print { 
        body { print-color-adjust: exact !important; -webkit-print-color-adjust: exact !important; }
        .no-print { display: none !important; }
      }
    </style></head>
    <body>
    <div class="no-print" style="background:#fff8e8;border:1px solid #f0c060;border-radius:8px;padding:12px 16px;margin:16px;font-family:Arial,sans-serif;font-size:13px;color:#7a5000;display:flex;align-items:center;gap:10px;">
      <span style="font-size:18px;">🖨️</span>
      <span><strong>For colour printing:</strong> In the print dialog click <strong>"More settings"</strong> → enable <strong>"Background graphics"</strong> → then print or save as PDF.</span>
    </div>
    <div class="page">
      <div class="header">
        <div class="header-left">
          <h1>${app.firstName || ''} ${app.lastName || ''}</h1>
          <p>Applied: ${app.appliedAt || '—'} &nbsp;|&nbsp; ${app.email || ''} &nbsp;|&nbsp; ${app.phone || ''}</p>
        </div>
        <div style="text-align:right">
          <div class="logo">${agency?.agencyName || 'Quikcare'}</div>
          <div style="margin-top:8px"><span class="status status-${app.status || 'pending'}">${app.status || 'pending'}</span></div>
        </div>
      </div>

      ${section('👤 Personal Details', [
        field('First Name', app.firstName),
        field('Last Name', app.lastName),
        field('Date of Birth', app.dob),
        field('Gender', app.gender),
        field('Nationality', app.nationality),
        app.religion ? field('Religion', app.religion) : '',
        field('NI Number', app.niNumber),
        field('Email', app.email),
        field('Phone', app.phone),
        field('Postcode', app.postcode),
        app.driving ? field('Driving Licence', app.driving) : '',
        app.languages?.length ? fieldFull('Languages Spoken', app.languages.join(', ')) : '',
      ])}

      ${section('🆘 Emergency Contact', [
        field('Name', app.emergencyName),
        field('Relationship', app.emergencyRelation),
        field('Phone', app.emergencyPhone),
        app.emergency2Name ? field('Name (2nd)', app.emergency2Name) : '',
        app.emergency2Rel  ? field('Relationship (2nd)', app.emergency2Rel) : '',
        app.emergency2Phone ? field('Phone (2nd)', app.emergency2Phone) : '',
      ])}

      ${section('🎓 Education & Training', [
        app.quals?.length ? tagsFull('Qualifications & Certifications', app.quals) : '',
        app.courses ? fieldFull('Other Courses / Training', app.courses) : '',
        app.firstAidExpiry ? field('First Aid Expiry', app.firstAidExpiry) : '',
        app.memberships ? field('Professional Memberships', app.memberships) : '',
      ])}

      ${section('💼 Experience & Care Standards', [
        app.settings?.length ? tagsFull('Care Settings', app.settings) : '',
        app.clients?.length ? tagsFull('Client Groups', app.clients) : '',
        app.experience ? fieldFull('Care Experience', app.experience) : '',
        app.whyCare ? fieldFull('Why Care?', app.whyCare) : '',
        app.strengths ? fieldFull('Key Strengths', app.strengths) : '',
      ])}

      ${app.employmentHistory?.length ? `<div class="section"><h2>🏢 Employment History</h2>
        ${app.employmentHistory.map((job, i) => {
          const jobFields = [
            field('Employer', job.employer),
            field('Job Title', job.jobTitle),
            field('From', job.from),
            field('To', job.to || 'Present'),
            field('Reason for Leaving', job.leaving),
            job.duties ? fieldFull('Duties & Responsibilities', job.duties) : '',
            job.gaps  ? fieldFull('Employment Gaps', job.gaps) : '',
          ].filter(Boolean).join('');
          return `<div style="background:#f8f5ff;border-radius:8px;padding:12px 16px;margin-bottom:8px"><div style="font-weight:700;color:#6C3FC5;margin-bottom:6px">Job ${i+1}</div><div class="grid">${jobFields}</div></div>`;
        }).join('')}
        ${app.employmentGaps ? `<div style="background:#fff8e8;border:1px solid #f0c060;border-radius:8px;padding:10px 14px;margin-top:8px"><div class="label">Employment Gaps / Additional Notes</div><div class="value">${app.employmentGaps}</div></div>` : ''}
      </div>` : ''}

      ${section('🏥 Health & Availability', [
        app.healthConditions ? fieldFull('Health Conditions / Disabilities', app.healthConditions) : '',
        app.adjustments ? fieldFull('Reasonable Adjustments', app.adjustments) : '',
        app.transport ? field('Transport', app.transport) : '',
        app.availableStart ? field('Available to Start', app.availableStart) : '',
        app.interviewAvail ? field('Interview Availability', app.interviewAvail) : '',
      ])}

      ${section('🛡️ Right to Work & DBS', [
        field('Right to Work Status', app.rightToWork),
        app.niNumber ? field('NI Number', app.niNumber) : '',
        app.docs?.length ? tagsFull('RTW Documents Held', app.docs) : '',
        app.shareCode ? field('Share Code', app.shareCode) : '',
        app.visaExpiry ? field('Visa Expiry', app.visaExpiry) : '',
        field('DBS Certificate Type', app.hasDbs),
        app.dbsNumber ? field('DBS Certificate Number', app.dbsNumber) : '',
        app.dbsDate ? field('DBS Issue Date', app.dbsDate) : '',
        app.updateService ? field('DBS Update Service', app.updateService) : '',
        app.updateServiceNum ? field('Update Service Number', app.updateServiceNum) : '',
        field('Criminal Convictions', app.conviction),
        app.convictionDetails ? fieldFull('Conviction Details', app.convictionDetails) : '',
        app.poa1URL ? field('Proof of Address 1', app.poa1Type || 'Uploaded') : '',
        app.poa2URL ? field('Proof of Address 2', app.poa2Type || 'Uploaded') : '',
      ])}

      ${app.bankName || app.bankInstitute || app.sortCode ? section('💰 Bank Details', [
        app.bankInstitute ? field('Bank / Building Society', app.bankInstitute) : '',
        field('Account Holder', app.bankName),
        field('Sort Code', app.sortCode),
        field('Account Number', app.accountNumber),
      ]) : ''}

      <div class="section"><h2>📎 Uploaded Documents</h2>
        <div style="background:#f8f5ff;border-radius:8px;padding:12px 16px;">
          ${freshCV ? `<div class="doc-row">📄 CV &nbsp;<a class="doc-link" href="${freshCV}" target="_blank">View</a></div>` : ''}
          ${freshPassport ? `<div class="doc-row">🛂 Passport &nbsp;<a class="doc-link" href="${freshPassport}" target="_blank">View</a></div>` : '<div class="doc-row" style="color:#cc0000">🛂 Passport — not uploaded</div>'}
          ${freshRTW ? `<div class="doc-row">📋 RTW Document &nbsp;<a class="doc-link" href="${freshRTW}" target="_blank">View</a></div>` : '<div class="doc-row" style="color:#cc0000">📋 RTW Document — not uploaded</div>'}
          ${freshPoa1 ? `<div class="doc-row">🏠 Proof of Address 1 (${app.proofAddress1 || ''}) &nbsp;<a class="doc-link" href="${freshPoa1}" target="_blank">View</a></div>` : '<div class="doc-row" style="color:#cc0000">🏠 Proof of Address 1 — not uploaded</div>'}
          ${freshPoa2 ? `<div class="doc-row">🏠 Proof of Address 2 (${app.proofAddress2 || ''}) &nbsp;<a class="doc-link" href="${freshPoa2}" target="_blank">View</a></div>` : '<div class="doc-row" style="color:#cc0000">🏠 Proof of Address 2 — not uploaded</div>'}
          ${freshDBS ? `<div class="doc-row">🔒 DBS Certificate &nbsp;<a class="doc-link" href="${freshDBS}" target="_blank">View</a></div>` : ''}
        </div>
      </div>

      ${(app.refs || []).length ? `<div class="section"><h2>📋 References</h2>
        ${(app.refs || []).map((r, i) => {
          const refFields = [
            field('Name', r.name),
            field('Job Title', r.title),
            field('Organisation', r.org),
            field('Email', r.email),
            field('Relationship', r.relation),
          ].filter(Boolean).join('');
          return `<div style="background:#f8f5ff;border-radius:8px;padding:12px 16px;margin-bottom:8px"><div style="font-weight:700;color:#6C3FC5;margin-bottom:6px">Reference ${i+1}</div><div class="grid">${refFields}</div></div>`;
        }).join('')}
      </div>` : ''}\

      ${app.signature ? `<div class="section"><h2>✍️ Declaration & Signature</h2>
        <div class="grid">
          <div class="field full" style="background:#f0ebff;border:1px solid #c5b3e8;">
            <div class="label">Declaration</div>
            <div class="value" style="font-size:12px;line-height:1.7;color:#4a4a6a;">I declare that the information given in this application is, to the best of my knowledge, true and complete. I understand that any false or misleading information may result in the withdrawal of any offer of employment, or dismissal if already in post.</div>
          </div>
          <div class="field">
            <div class="label">Signature</div>
            <div class="value" style="font-style:italic;font-size:16px;font-family:Georgia,serif;">${app.signature}</div>
          </div>
          <div class="field">
            <div class="label">Date Signed</div>
            <div class="value">${app.signedAt || app.appliedAt || '—'}</div>
          </div>
        </div>
      </div>` : ''}

      <div class="footer">
        <span>${agency?.agencyName || 'Quikcare'} — Powered by Quikcare Ltd · Co. No. 17206901</span>
        <span>Generated: ${new Date().toLocaleDateString('en-GB')} &nbsp;|&nbsp; ID: ${app.id || '—'}</span>
      </div>
    </div></div></body></html>`;
    const win = window.open('', '_blank');
    win.document.write(html);
    win.document.close();
    setTimeout(() => win.print(), 500);
  };
  return (
    <div style={s.modal} onClick={onClose}>
      <div style={s.modalBox} onClick={e => e.stopPropagation()}>
        <div style={s.modalHeader}>
          <div>
            <div style={s.modalTitle}>{app.firstName} {app.lastName}</div>
            <div style={{ fontSize: 12, color: "#9b7fd4", marginTop: 4 }}>Applied {app.appliedAt} · {app.postcode}</div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span style={s.statusPill(app.status)}>{STATUS_LABELS[app.status]}</span>
            <button style={s.closeBtn} onClick={onClose}>✕</button>
          </div>
        </div>
        <div style={s.modalBody}>
          <div style={s.section}>
            <div style={s.sectionTitle}>👤 Personal Details</div>
            <div style={s.detailGrid}>
              <DetailItem label="Email" value={app.email} />
              <DetailItem label="Phone" value={app.phone} />
              <DetailItem label="Date of Birth" value={app.dob} />
              <DetailItem label="NI Number" value={app.niNumber} />
              <DetailItem label="Driving Licence" value={app.driving} />
              <DetailItem label="Nationality" value={app.nationality} />
              <DetailItem label="Gender" value={app.gender} />
              <DetailItem label="Languages" value={app.languages?.join(", ")} />
            </div>
          </div>
          <div style={s.section}>
            <div style={s.sectionTitle}>💼 Experience</div>
            <div style={s.detailGrid}>
              <DetailItem label="NI Number" value={app.niNumber} />
              <DetailItem label="Transport" value={app.transport} />
            </div>
            {app.settings?.length > 0 && <div style={{ marginTop: 10 }}><div style={s.detailLabel}>Care Settings</div><div style={s.tagRow}>{app.settings.map(t => <span key={t} style={s.tag}>{t}</span>)}</div></div>}
            {app.clients?.length > 0 && <div style={{ marginTop: 10 }}><div style={s.detailLabel}>Client Groups</div><div style={s.tagRow}>{app.clients.map(t => <span key={t} style={s.tag}>{t}</span>)}</div></div>}
            {app.quals?.length > 0 && <div style={{ marginTop: 10 }}><div style={s.detailLabel}>Qualifications</div><div style={s.tagRow}>{app.quals.map(t => <span key={t} style={s.tag}>{t}</span>)}</div></div>}
          </div>
          <div style={s.section}>
            <div style={s.sectionTitle}>📋 Right to Work</div>
            <div style={s.detailGrid}>
              <DetailItem label="Status" value={app.rightToWork} />
              <DetailItem label="DBS Type" value={app.hasDbs} />
              <DetailItem label="Convictions" value={app.conviction} />
              <DetailItem label="Documents Held" value={app.docs?.join(", ")} />
            </div>
          </div>
          {(app.cvURL || app.poa1URL || app.poa2URL || app.rtwDocURL) && (
            <div style={s.section}>
              <div style={s.sectionTitle}>📁 Uploaded Documents</div>
              <div style={s.tagRow}>
                {app.cvURL && <button onClick={() => openDoc(app.cvURL, "CV")} style={{ padding: "6px 14px", borderRadius: 999, background: "#f0ebff", border: "1px solid #c5b3e8", color: "#6C3FC5", fontSize: 13, cursor: "pointer" }}>📄 CV</button>}
                {app.passportURL && <button onClick={() => openDoc(app.passportURL, "Passport")} style={{ padding: "6px 14px", borderRadius: 999, background: "#f0ebff", border: "1px solid #c5b3e8", color: "#6C3FC5", fontSize: 13, cursor: "pointer" }}>🛂 Passport</button>}
                {app.poa1URL && <button onClick={() => openDoc(app.poa1URL, "Proof of Address 1")} style={{ padding: "6px 14px", borderRadius: 999, background: "#f0ebff", border: "1px solid #c5b3e8", color: "#6C3FC5", fontSize: 13, cursor: "pointer" }}>🏠 Address 1</button>}
                {app.poa2URL && <button onClick={() => openDoc(app.poa2URL, "Proof of Address 2")} style={{ padding: "6px 14px", borderRadius: 999, background: "#f0ebff", border: "1px solid #c5b3e8", color: "#6C3FC5", fontSize: 13, cursor: "pointer" }}>🏠 Address 2</button>}
                {app.rtwDocURL && <button onClick={() => openDoc(app.rtwDocURL, "Right to Work")} style={{ padding: "6px 14px", borderRadius: 999, background: "#f0ebff", border: "1px solid #c5b3e8", color: "#6C3FC5", fontSize: 13, cursor: "pointer" }}>📋 Right to Work</button>}
                {app.dbsDocURL && <button onClick={() => openDoc(app.dbsDocURL, "DBS Certificate")} style={{ padding: "6px 14px", borderRadius: 999, background: "#f0ebff", border: "1px solid #c5b3e8", color: "#6C3FC5", fontSize: 13, cursor: "pointer" }}>🔒 DBS</button>}
              </div>
            </div>
          )}
          <div style={s.section}>
            <div style={s.sectionTitle}>⭐ References</div>
            {app.refs?.map((r, i) => (
              <div key={i} style={{ ...s.detailItem, marginBottom: 8 }}>
                <div style={{ fontSize: 13, color: "#1a1a2e", fontWeight: 600 }}>{r.name}</div>
                <div style={{ fontSize: 12, color: "#9b7fd4" }}>{r.title} · {r.org}</div>
                <div style={{ fontSize: 12, color: "#9b7fd4" }}>{r.email}</div>
              </div>
            ))}
          </div>
        </div>
        <div style={s.actionRow}>
          <button style={s.rejectBtn} onClick={() => { onReject(app.id); onClose(); }}>✕ Reject</button>
          <button style={s.pdfBtn} onClick={downloadPDF}>📄 PDF</button>
          <button style={s.approveBtn} onClick={() => { onApprove(app.id); onClose(); }}>✓ Approve</button>
        </div>
        <div style={{ padding: "12px 24px", borderTop: "1px solid #f0ebff", background: "#fff8f8", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={{ fontSize: 11, color: "#9b7fd4" }}>🔒 GDPR: Right to Erasure</span>
          <button style={{ background: "none", border: "1px solid #ffb3b3", borderRadius: 6, padding: "6px 14px", color: "#cc0000", fontSize: 12, cursor: "pointer" }}
            onClick={() => { onDelete(app.id, `${app.firstName} ${app.lastName}`); onClose(); }}>
            🗑 Delete Application Data
          </button>
        </div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// COMPLIANCE PANEL — CQC Regulation 19 & Schedule 3 pre-employment checks
// ──────────────────────────────────────────────────────────────────────────────
const CHECK_DEFS = [
  { id: "identity",   label: "Identity Verified",         icon: "🪪", section: "identity" },
  { id: "rtw",        label: "Right to Work Confirmed",   icon: "✈️", section: "rtw" },
  { id: "dbs",        label: "DBS Check Obtained",        icon: "🔒", section: "dbs" },
  { id: "barred",     label: "Barred List Checked",       icon: "🚫", section: "dbs" },
  { id: "empHistory", label: "Employment History Verified",icon: "🏢", section: "employment" },
  { id: "gaps",       label: "Employment Gaps Explained", icon: "📅", section: "employment" },
  { id: "ref1",       label: "Reference 1 Received",      icon: "⭐", section: "references" },
  { id: "ref2",       label: "Reference 2 Received",      icon: "⭐", section: "references" },
  { id: "quals",      label: "Qualifications Verified",   icon: "🎓", section: "quals" },
  { id: "health",     label: "Health Declaration Obtained",icon: "🏥", section: "health" },
  { id: "interview",  label: "Interview Conducted",       icon: "🗣️", section: "interview" },
];

const CHECK_STATUS_COLORS = {
  verified:     { bg: "#e8f5eb", text: "#1a7a3a", border: "#a3d9b1", icon: "✅" },
  pending:      { bg: "#f5f0ff", text: "#6C3FC5", border: "#c5b3e8", icon: "⏳" },
  issue:        { bg: "#fff0f0", text: "#cc0000", border: "#ffb3b3", icon: "⚠️" },
  na:           { bg: "#f8f8f8", text: "#888",    border: "#ddd",    icon: "—" },
};

const DECISION_COLORS = {
  cleared:     { bg: "#e8f5eb", text: "#1a7a3a", border: "#a3d9b1" },
  conditional: { bg: "#fff8e8", text: "#7a5000", border: "#f0c060" },
  notcleared:  { bg: "#fff0f0", text: "#cc0000", border: "#ffb3b3" },
};

function ComplianceChecker({ app, onBack, onSave }) {
  const [checks, setChecks] = useState({});
  const [notes, setNotes]   = useState({});
  const [decision, setDecision] = useState("");
  const [decisionNote, setDecisionNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved]   = useState(false);
  // Manual overrides for pre-filled application fields
  const [overrides, setOverrides] = useState({});
  const setOverride = (key, val) => setOverrides(prev => ({ ...prev, [key]: val }));
  // Merge: override takes priority over application data
  const field = (key, appVal) => overrides[key] !== undefined ? overrides[key] : (appVal || "");

  // Load saved compliance data on mount
  useEffect(() => {
    const comp = app.compliance || app._compliance;
    if (comp) {
      setChecks(comp.checks || {});
      setNotes(comp.notes   || {});
      setDecision(comp.decision || "");
      setDecisionNote(comp.decisionNote || "");
      setOverrides(comp.overrides || {});
    }
  }, [app.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const setCheck = (id, val) => setChecks(prev => ({ ...prev, [id]: val }));
  const setNote  = (id, val) => setNotes(prev => ({ ...prev, [id]: val }));

  const completedCount = CHECK_DEFS.filter(c => checks[c.id] === "verified" || checks[c.id] === "na").length;
  const issueCount     = CHECK_DEFS.filter(c => checks[c.id] === "issue").length;

  const downloadCompliancePDF = () => {
    const statusIcon = (s) => ({ verified: "✅ Verified", pending: "⏳ Pending", issue: "⚠️ Issue", na: "— N/A" }[s] || "⏳ Pending");
    const statusColor = (s) => ({ verified: "#1a7a3a", pending: "#6C3FC5", issue: "#cc0000", na: "#888" }[s] || "#6C3FC5");
    const statusBg = (s) => ({ verified: "#e8f5eb", pending: "#f5f0ff", issue: "#fff0f0", na: "#f8f8f8" }[s] || "#f5f0ff");
    const decisionLabel = { cleared: "✅ Cleared to Start", conditional: "⚠️ Conditional Start", notcleared: "❌ Not Cleared" };
    const decisionColor = { cleared: "#1a7a3a", conditional: "#7a5000", notcleared: "#cc0000" };
    const decisionBg    = { cleared: "#e8f5eb",  conditional: "#fff8e8",  notcleared: "#fff0f0" };

    const checkRows = CHECK_DEFS.map(c => {
      const st = checks[c.id] || "pending";
      const note = notes[c.id] || "";
      return `
        <tr>
          <td style="padding:10px 14px;border-bottom:1px solid #f0ebff;font-size:13px;">${c.icon} ${c.label}</td>
          <td style="padding:10px 14px;border-bottom:1px solid #f0ebff;">
            <span style="display:inline-block;padding:3px 10px;border-radius:999px;font-size:11px;font-weight:700;background:${statusBg(st)};color:${statusColor(st)}">${statusIcon(st)}</span>
          </td>
          <td style="padding:10px 14px;border-bottom:1px solid #f0ebff;font-size:12px;color:#555;">${note || ""}</td>
        </tr>`;
    }).join("");

    const empRows = (app.employmentHistory || []).map((job, i) => `
      <div style="background:#f8f5ff;border-radius:6px;padding:10px 14px;margin-bottom:8px;">
        <div style="font-weight:700;font-size:13px;color:#1a1a2e;">${job.jobTitle} — ${job.employer}</div>
        <div style="font-size:12px;color:#9b7fd4;margin-top:2px;">${job.from || ""} → ${job.to || "Present"}${job.leaving ? " · Left: " + job.leaving : ""}</div>
        ${job.gaps ? `<div style="margin-top:4px;font-size:12px;background:#fff8e8;border:1px solid #f0c060;border-radius:4px;padding:4px 8px;color:#7a5000;">Gap: ${job.gaps}</div>` : ""}
      </div>`).join("");

    const refRows = (app.refs || []).map((r, i) => `
      <div style="background:#f8f5ff;border-radius:6px;padding:10px 14px;margin-bottom:8px;">
        <div style="font-weight:700;font-size:13px;">${r.name} — ${r.title || r.position || ""}</div>
        <div style="font-size:12px;color:#9b7fd4;">${r.org || ""} · ${r.email || ""} · ${r.relation || r.relationship || ""}</div>
      </div>`).join("");

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8">
    <title>Compliance Report — ${app.firstName} ${app.lastName}</title>
    <style>
      * { box-sizing: border-box; margin: 0; padding: 0; }
      body { font-family: Arial, sans-serif; color: #1a1a2e; font-size: 13px; line-height: 1.5; }
      .page { padding: 32px 40px; max-width: 900px; margin: 0 auto; }
      .header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #6C3FC5; padding-bottom: 16px; margin-bottom: 24px; }
      .logo { font-size: 20px; font-weight: 700; color: #6C3FC5; }
      h1 { font-size: 22px; color: #6C3FC5; margin-bottom: 4px; }
      h2 { font-size: 11px; color: #6C3FC5; text-transform: uppercase; letter-spacing: 0.1em; border-bottom: 1px solid #e8e0f5; padding-bottom: 6px; margin-bottom: 12px; font-weight: 700; }
      .section { margin-bottom: 24px; page-break-inside: avoid; }
      .infoGrid { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 8px; margin-bottom: 16px; }
      .infoCell { background: #f8f5ff; border-radius: 6px; padding: 8px 12px; }
      .infoLabel { font-size: 9px; color: #9b7fd4; text-transform: uppercase; letter-spacing: 0.08em; margin-bottom: 2px; }
      .infoVal { font-size: 12px; color: #1a1a2e; }
      table { width: 100%; border-collapse: collapse; }
      th { text-align: left; padding: 8px 14px; font-size: 10px; color: #9b7fd4; text-transform: uppercase; letter-spacing: 0.08em; border-bottom: 2px solid #e8e0f5; }
      .decision { border-radius: 8px; padding: 14px 18px; margin-bottom: 8px; }
      .sigRow { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-top: 20px; }
      .sigBox { border-top: 1px solid #1a1a2e; padding-top: 8px; font-size: 11px; color: #9b7fd4; }
      .footer { margin-top: 32px; padding-top: 12px; border-top: 1px solid #e8e0f5; display: flex; justify-content: space-between; font-size: 10px; color: #9b7fd4; }
      * { print-color-adjust: exact !important; -webkit-print-color-adjust: exact !important; }
      @media print { .no-print { display: none !important; } }
    </style></head><body>
    <div class="no-print" style="background:#fff8e8;border:1px solid #f0c060;border-radius:8px;padding:12px 16px;margin:16px;font-size:13px;color:#7a5000;display:flex;align-items:center;gap:10px;">
      <span style="font-size:18px;">🖨️</span>
      <span><strong>For colour printing:</strong> In the print dialog click <strong>"More settings"</strong> → enable <strong>"Background graphics"</strong> → then print or save as PDF.</span>
    </div>
    <div class="page">
      <div class="header">
        <div>
          <h1>${app.firstName} ${app.lastName}</h1>
          <p style="color:#9b7fd4;font-size:12px;">Pre-Employment Compliance Report &nbsp;·&nbsp; Applied: ${app.appliedAt || "—"} &nbsp;·&nbsp; ${app.email || ""}</p>
        </div>
        <div style="text-align:right">
          <div class="logo">Quikcare</div>
          <div style="font-size:11px;color:#9b7fd4;margin-top:4px;">CQC Regulation 19 &amp; Schedule 3</div>
        </div>
      </div>

      <div class="section">
        <h2>📋 Application Details</h2>
        <div class="infoGrid">
          ${[
            ["Right to Work",      field("rightToWork",      app.rightToWork)],
            ["NI Number",          field("niNumber",         app.niNumber)],
            ["Nationality",        field("nationality",      app.nationality)],
            ["Share Code",         field("shareCode",        app.shareCode)],
            ["Visa Expiry",        field("visaExpiry",       app.visaExpiry)],
            ["DBS Type",           field("hasDbs",           app.hasDbs)],
            ["DBS Number",         field("dbsNumber",        app.dbsNumber)],
            ["DBS Issue Date",     field("dbsDate",          app.dbsDate)],
            ["DBS Update Service", field("updateService",    app.updateService)],
            ["Update Service No.", field("updateServiceNum", app.updateServiceNum)],
            ["Criminal Convictions", field("conviction",     app.conviction)],
            ["References Provided",  (app.refs || []).length + " reference(s)"],
            ["Employment History",   (app.employmentHistory || []).length + " role(s)"],
            ["Qualifications",     field("qualifications",   (app.quals || []).join(", ")) || "None"],
            ["First Aid Expiry",   field("firstAidExpiry",   app.firstAidExpiry)],
            ["Health Declaration", field("healthConditions", app.healthConditions) || "None declared"],
          ].map(([l, v]) => `<div class="infoCell"><div class="infoLabel">${l}</div><div class="infoVal">${v || "—"}</div></div>`).join("")}
        </div>
      </div>

      <div class="section">
        <h2>✅ Pre-Employment Checks</h2>
        <table>
          <thead><tr><th>Check</th><th>Status</th><th>Notes</th></tr></thead>
          <tbody>${checkRows}</tbody>
        </table>
      </div>

      ${empRows ? `<div class="section"><h2>🏢 Employment History</h2>${empRows}</div>` : ""}
      ${refRows ? `<div class="section"><h2>⭐ References</h2>${refRows}</div>` : ""}

      <div class="section">
        <h2>⚖️ Compliance Decision</h2>
        ${decision ? `
          <div class="decision" style="background:${decisionBg[decision]};border:1px solid ${decisionColor[decision]}33;">
            <div style="font-size:16px;font-weight:700;color:${decisionColor[decision]};margin-bottom:6px;">${decisionLabel[decision]}</div>
            ${decisionNote ? `<div style="font-size:13px;color:#333;line-height:1.6;">${decisionNote}</div>` : ""}
          </div>` : `<div style="color:#cc0000;font-size:13px;padding:12px;background:#fff0f0;border-radius:6px;">No decision recorded yet</div>`}
      </div>

      <div class="section">
        <h2>✍️ Authorisation</h2>
        <div class="sigRow">
          <div class="sigBox">Completed by (print name)</div>
          <div class="sigBox">Signature &amp; Date</div>
        </div>
        <div style="margin-top:12px;font-size:11px;color:#9b7fd4;">
          I confirm that the above pre-employment checks have been completed in accordance with CQC Regulation 19 and Schedule 3 of the Health and Social Care Act 2008 (Regulated Activities) Regulations 2014.
        </div>
      </div>

      <div class="footer">
        <span>Quikcare Ltd · Co. No. 17206901 · CQC Reg. 19 &amp; Schedule 3 Compliance</span>
        <span>Generated: ${new Date().toLocaleDateString("en-GB")} &nbsp;·&nbsp; ID: ${app.id || "—"}</span>
      </div>
    </div></body></html>`;

    const win = window.open("", "_blank");
    win.document.write(html);
    win.document.close();
    setTimeout(() => win.print(), 500);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const data = { checks, notes, decision, decisionNote, overrides, updatedAt: new Date().toISOString() };
      await onSave(app.id, data);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      console.error("Compliance save error:", err);
      alert("Save failed: " + (err.message || "Unknown error"));
    } finally {
      setSaving(false);
    }
  };

  const cs = {
    wrap:      { maxWidth: 860, margin: "0 auto", padding: "24px 16px" },
    backBtn:   { background: "none", border: "none", color: "#9b7fd4", fontSize: 14, cursor: "pointer", marginBottom: 16, display: "flex", alignItems: "center", gap: 6 },
    card:      { background: "#fff", border: "1px solid #e8e0f5", borderRadius: 12, marginBottom: 16, overflow: "hidden" },
    cardHead:  { background: "#f8f5ff", padding: "12px 20px", fontWeight: 700, fontSize: 13, color: "#6C3FC5", borderBottom: "1px solid #e8e0f5" },
    checkRow:  { display: "flex", alignItems: "flex-start", gap: 12, padding: "14px 20px", borderBottom: "1px solid #f0ebff" },
    checkIcon: { fontSize: 20, width: 28, flexShrink: 0, paddingTop: 2 },
    checkLabel:{ flex: 1, fontSize: 14, color: "#1a1a2e", fontWeight: 500 },
    statusBtns:{ display: "flex", gap: 6, flexShrink: 0 },
    statusBtn: (active, color) => ({
      padding: "4px 10px", borderRadius: 999, fontSize: 11, fontWeight: 600, cursor: "pointer",
      background: active ? color.bg : "#f8f8f8",
      color: active ? color.text : "#aaa",
      border: `1px solid ${active ? color.border : "#e0e0e0"}`,
    }),
    noteInput:  { width: "100%", marginTop: 8, padding: "6px 10px", borderRadius: 6, border: "1px solid #e8e0f5", fontSize: 12, color: "#1a1a2e", outline: "none", resize: "vertical", minHeight: 36 },
    infoBox:    { background: "#f8f5ff", borderRadius: 8, padding: "10px 14px", marginTop: 4, fontSize: 12, color: "#6C3FC5" },
    summaryBar: { display: "flex", alignItems: "center", justifyContent: "space-between", background: "#fff", border: "1px solid #e8e0f5", borderRadius: 12, padding: "16px 20px", marginBottom: 20 },
    saveBtn:    { padding: "12px 28px", background: "#6C3FC5", border: "none", borderRadius: 8, color: "white", fontSize: 14, fontWeight: 700, cursor: "pointer" },
  };

  const renderCheckRow = (check) => {
    const status = checks[check.id] || "pending";
    const note   = notes[check.id]  || "";
    return (
      <div key={check.id} style={{ borderBottom: "1px solid #f0ebff" }}>
        <div style={cs.checkRow}>
          <span style={cs.checkIcon}>{check.icon}</span>
          <div style={cs.checkLabel}>{check.label}</div>
          <div style={cs.statusBtns}>
            {Object.entries(CHECK_STATUS_COLORS).map(([key, col]) => (
              <button key={key} style={cs.statusBtn(status === key, col)}
                onClick={() => setCheck(check.id, key)}>
                {col.icon} {key === "na" ? "N/A" : key.charAt(0).toUpperCase() + key.slice(1)}
              </button>
            ))}
          </div>
        </div>
        <div style={{ padding: "0 20px 12px 60px" }}>
          <textarea style={cs.noteInput} placeholder="Notes (optional)..." rows={1}
            value={note} onChange={e => setNote(check.id, e.target.value)} />
        </div>
      </div>
    );
  };

  const sections = {
    identity:   { title: "🪪 Identity", checks: ["identity"] },
    rtw:        { title: "✈️ Right to Work", checks: ["rtw"] },
    dbs:        { title: "🔒 DBS & Barred List", checks: ["dbs", "barred"] },
    employment: { title: "🏢 Employment History", checks: ["empHistory", "gaps"] },
    references: { title: "⭐ References", checks: ["ref1", "ref2"] },
    quals:      { title: "🎓 Qualifications", checks: ["quals"] },
    health:     { title: "🏥 Health", checks: ["health"] },
    interview:  { title: "🗣️ Interview", checks: ["interview"] },
  };

  return (
    <div style={cs.wrap}>
      <button style={cs.backBtn} onClick={onBack}>← Back to Compliance List</button>

      <div style={{ marginBottom: 20 }}>
        <h2 style={{ fontFamily: "'DM Serif Display', serif", fontSize: 22, color: "#1a1a2e", marginBottom: 4 }}>
          {app.firstName} {app.lastName}
        </h2>
        <div style={{ fontSize: 13, color: "#9b7fd4" }}>
          {app.email} &nbsp;·&nbsp; Applied {app.appliedAt}
        </div>
      </div>

      {/* Pre-filled info from application — all fields editable */}
      <div style={cs.card}>
        <div style={{ ...cs.cardHead, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>📋 Application Details</span>
          <span style={{ fontSize: 11, fontWeight: 400, color: "#9b7fd4" }}>Pre-filled from form · click any field to edit</span>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 0 }}>
          {[
            ["rightToWork",      "Right to Work",       app.rightToWork],
            ["hasDbs",           "DBS Type",            app.hasDbs],
            ["dbsNumber",        "DBS Number",          app.dbsNumber],
            ["dbsDate",          "DBS Issue Date",      app.dbsDate],
            ["updateService",    "DBS Update Service",  app.updateService],
            ["updateServiceNum", "Update Service No.",  app.updateServiceNum],
            ["conviction",       "Convictions",         app.conviction],
            ["niNumber",         "NI Number",           app.niNumber],
            ["nationality",      "Nationality",         app.nationality],
            ["shareCode",        "Share Code",          app.shareCode],
            ["visaExpiry",       "Visa Expiry",         app.visaExpiry],
            ["qualifications",   "Qualifications",      app.quals?.join(", ")],
            ["healthConditions", "Health Declaration",  app.healthConditions],
            ["firstAidExpiry",   "First Aid Expiry",    app.firstAidExpiry],
            ["memberships",      "Prof. Memberships",   app.memberships],
          ].map(([key, label, appVal]) => {
            const val = field(key, appVal);
            const isOverridden = overrides[key] !== undefined && overrides[key] !== (appVal || "");
            return (
              <div key={key} style={{ padding: "10px 14px", borderBottom: "1px solid #f0ebff", borderRight: "1px solid #f0ebff" }}>
                <div style={{ fontSize: 10, color: "#9b7fd4", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4, display: "flex", justifyContent: "space-between" }}>
                  <span>{label}</span>
                  {isOverridden && <span style={{ color: "#6C3FC5", fontSize: 9 }}>✏️ edited</span>}
                </div>
                <input
                  value={val}
                  onChange={e => setOverride(key, e.target.value)}
                  placeholder={appVal ? "" : "Enter manually..."}
                  style={{
                    width: "100%", border: "none", outline: "none", background: "transparent",
                    fontSize: 12, color: val ? "#1a1a2e" : "#bbb",
                    borderBottom: `1px dashed ${isOverridden ? "#6C3FC5" : "#e0e0e0"}`,
                    paddingBottom: 2,
                  }}
                />
              </div>
            );
          })}
        </div>
      </div>

      {/* Checks by section */}
      {Object.entries(sections).map(([secKey, sec]) => (
        <div key={secKey} style={cs.card}>
          <div style={cs.cardHead}>{sec.title}</div>
          {sec.checks.map(id => renderCheckRow(CHECK_DEFS.find(c => c.id === id)))}
        </div>
      ))}

      {/* Employment history detail */}
      {app.employmentHistory?.length > 0 && (
        <div style={cs.card}>
          <div style={cs.cardHead}>🏢 Employment Timeline (from application)</div>
          {app.employmentHistory.map((job, i) => (
            <div key={i} style={{ padding: "12px 20px", borderBottom: "1px solid #f0ebff" }}>
              <div style={{ fontWeight: 600, fontSize: 13, color: "#1a1a2e" }}>{job.jobTitle} — {job.employer}</div>
              <div style={{ fontSize: 12, color: "#9b7fd4", marginTop: 2 }}>{job.from} → {job.to || "Present"}{job.leaving ? ` · Left: ${job.leaving}` : ""}</div>
              {job.gaps && <div style={{ marginTop: 4, fontSize: 12, background: "#fff8e8", border: "1px solid #f0c060", borderRadius: 6, padding: "4px 10px", color: "#7a5000" }}>⚠️ Gap noted: {job.gaps}</div>}
            </div>
          ))}
        </div>
      )}

      {/* References detail */}
      {app.refs?.length > 0 && (
        <div style={cs.card}>
          <div style={cs.cardHead}>⭐ References (from application)</div>
          {app.refs.map((r, i) => (
            <div key={i} style={{ padding: "12px 20px", borderBottom: "1px solid #f0ebff" }}>
              <div style={{ fontWeight: 600, fontSize: 13 }}>{r.name} — {r.title} at {r.org}</div>
              <div style={{ fontSize: 12, color: "#9b7fd4" }}>{r.email} &nbsp;·&nbsp; {r.relation}</div>
            </div>
          ))}
        </div>
      )}

      {/* Decision */}
      <div style={cs.card}>
        <div style={cs.cardHead}>⚖️ Compliance Decision</div>
        <div style={{ padding: 20 }}>
          <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>
            {[
              { key: "cleared",     label: "✅ Cleared to Start" },
              { key: "conditional", label: "⚠️ Conditional Start" },
              { key: "notcleared", label: "❌ Not Cleared" },
            ].map(d => (
              <button key={d.key} onClick={() => setDecision(d.key)}
                style={{
                  flex: 1, padding: "12px", borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: "pointer",
                  background: decision === d.key ? DECISION_COLORS[d.key].bg : "#f8f8f8",
                  color: decision === d.key ? DECISION_COLORS[d.key].text : "#aaa",
                  border: `1px solid ${decision === d.key ? DECISION_COLORS[d.key].border : "#e0e0e0"}`,
                }}>
                {d.label}
              </button>
            ))}
          </div>
          <textarea style={{ ...cs.noteInput, minHeight: 80 }}
            placeholder="Decision rationale / conditions / notes..."
            value={decisionNote} onChange={e => setDecisionNote(e.target.value)} />
        </div>
      </div>

      {/* Summary bar + save */}
      <div style={cs.summaryBar}>
        <div style={{ display: "flex", gap: 20, fontSize: 13, color: "#9b7fd4" }}>
          <span>✅ {completedCount}/{CHECK_DEFS.length} checks done</span>
          {issueCount > 0 && <span style={{ color: "#cc0000" }}>⚠️ {issueCount} issue{issueCount > 1 ? "s" : ""}</span>}
          {decision && <span style={{ color: DECISION_COLORS[decision]?.text, fontWeight: 700 }}>
            {decision === "cleared" ? "✅ Cleared" : decision === "conditional" ? "⚠️ Conditional" : "❌ Not Cleared"}
          </span>}
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button style={{ padding: "12px 16px", borderRadius: 8, border: "1px solid #c5b3e8", background: "#f0ebff", color: "#6C3FC5", fontSize: 14, cursor: "pointer" }}
            onClick={downloadCompliancePDF}>
            📄 Download Report
          </button>
          <button style={cs.saveBtn} onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : saved ? "✓ Saved!" : "💾 Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

function CompliancePanel({ agency, applications }) {
  const [selected, setSelected] = useState(null);
  const [complianceData, setComplianceData] = useState({});
  const [search, setSearch] = useState("");

  // Compliance data is stored on the application doc itself under `compliance` field
  // so we just read it from the already-loaded applications array
  useEffect(() => {
    const result = {};
    applications.forEach(app => {
      if (app.compliance) result[app.id] = app.compliance;
    });
    setComplianceData(result);
  }, [applications]);

  const saveCompliance = async (appId, data) => {
    await updateDoc(doc(db, "applications", appId), { compliance: data });
    setComplianceData(prev => ({ ...prev, [appId]: data }));
  };

  const filtered = applications.filter(a => {
    if (!search) return true;
    return `${a.firstName} ${a.lastName} ${a.email}`.toLowerCase().includes(search.toLowerCase());
  });

  if (selected) {
    const comp = complianceData[selected.id] || {};
    const appWithCompliance = { ...selected, compliance: comp };
    return (
      <div style={{ background: "#f8f5ff", minHeight: "100vh" }}>
        <ComplianceChecker app={appWithCompliance} onBack={() => setSelected(null)} onSave={saveCompliance} />
      </div>
    );
  }

  const getComplianceProgress = (appId) => {
    const comp = complianceData[appId];
    if (!comp?.checks) return { done: 0, total: CHECK_DEFS.length, decision: null };
    const done = CHECK_DEFS.filter(c => comp.checks[c.id] === "verified" || comp.checks[c.id] === "na").length;
    return { done, total: CHECK_DEFS.length, decision: comp.decision };
  };

  return (
    <div style={{ maxWidth: 1100, margin: "0 auto", padding: "24px 16px" }}>
      <div style={{ marginBottom: 20, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
        <div>
          <h2 style={{ fontFamily: "'DM Serif Display', serif", fontSize: 22, color: "#1a1a2e", marginBottom: 4 }}>✅ Compliance Tracker</h2>
          <p style={{ fontSize: 13, color: "#9b7fd4" }}>CQC Regulation 19 &amp; Schedule 3 — Pre-employment checks</p>
        </div>
        <input
          style={{ padding: "10px 16px", border: "1px solid #e8e0f5", borderRadius: 8, fontSize: 14, outline: "none", width: 260, background: "#fff" }}
          placeholder="🔍 Search applicants..."
          value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      {filtered.length === 0 ? (
        <div style={{ textAlign: "center", padding: "60px 20px", color: "#9b7fd4", fontSize: 14 }}>No applications to show</div>
      ) : (
        <div style={{ background: "#fff", border: "1px solid #e8e0f5", borderRadius: 12, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#f8f5ff" }}>
                {["Applicant", "Applied", "Application Status", "Checks Complete", "Compliance Decision", ""].map(h => (
                  <th key={h} style={{ textAlign: "left", padding: "10px 16px", fontSize: 11, color: "#9b7fd4", textTransform: "uppercase", letterSpacing: "0.08em", borderBottom: "1px solid #e8e0f5", fontWeight: 600 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(app => {
                const { done, total, decision } = getComplianceProgress(app.id);
                const pct = Math.round((done / total) * 100);
                return (
                  <tr key={app.id} style={{ cursor: "pointer", transition: "background 0.15s" }}
                    onMouseEnter={e => e.currentTarget.style.background = "#f8f5ff"}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                    onClick={() => setSelected(app)}>
                    <td style={{ padding: "14px 16px", fontSize: 14, borderBottom: "1px solid #f0ebff" }}>
                      <div style={{ fontWeight: 600 }}>{app.firstName} {app.lastName}</div>
                      <div style={{ fontSize: 12, color: "#9b7fd4", marginTop: 2 }}>{app.email}</div>
                    </td>
                    <td style={{ padding: "14px 16px", fontSize: 13, color: "#9b7fd4", borderBottom: "1px solid #f0ebff" }}>{app.appliedAt || "—"}</td>
                    <td style={{ padding: "14px 16px", borderBottom: "1px solid #f0ebff" }}>
                      <span style={{ display: "inline-block", padding: "4px 10px", borderRadius: 999, fontSize: 11, fontWeight: 600,
                        background: STATUS_COLORS[app.status]?.bg, color: STATUS_COLORS[app.status]?.text, border: `1px solid ${STATUS_COLORS[app.status]?.border}` }}>
                        {STATUS_LABELS[app.status] || app.status}
                      </span>
                    </td>
                    <td style={{ padding: "14px 16px", borderBottom: "1px solid #f0ebff" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <div style={{ flex: 1, height: 6, background: "#e8e0f5", borderRadius: 3, overflow: "hidden", minWidth: 80 }}>
                          <div style={{ height: "100%", width: `${pct}%`, background: pct === 100 ? "#1a7a3a" : "#6C3FC5", borderRadius: 3, transition: "width 0.3s" }} />
                        </div>
                        <span style={{ fontSize: 12, color: "#9b7fd4", whiteSpace: "nowrap" }}>{done}/{total}</span>
                      </div>
                    </td>
                    <td style={{ padding: "14px 16px", borderBottom: "1px solid #f0ebff" }}>
                      {decision ? (
                        <span style={{ display: "inline-block", padding: "4px 10px", borderRadius: 999, fontSize: 11, fontWeight: 700,
                          background: DECISION_COLORS[decision]?.bg, color: DECISION_COLORS[decision]?.text, border: `1px solid ${DECISION_COLORS[decision]?.border}` }}>
                          {decision === "cleared" ? "✅ Cleared" : decision === "conditional" ? "⚠️ Conditional" : "❌ Not Cleared"}
                        </span>
                      ) : <span style={{ fontSize: 12, color: "#ccc" }}>Not started</span>}
                    </td>
                    <td style={{ padding: "14px 16px", borderBottom: "1px solid #f0ebff" }}>
                      <button style={{ padding: "6px 14px", borderRadius: 8, border: "1px solid #c5b3e8", background: "#f0ebff", color: "#6C3FC5", fontSize: 12, cursor: "pointer", fontWeight: 600 }}>
                        {done > 0 ? "Continue →" : "Start →"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function exportCSV(applications) {
  const headers = ["Name", "Email", "Phone", "Postcode", "Applied", "Experience", "DBS", "Status"];
  const rows = applications.map(a => [`${a.firstName} ${a.lastName}`, a.email, a.phone, a.postcode, a.appliedAt, a.years, a.hasDbs, a.status]);
  const csv = [headers, ...rows].map(r => r.map(v => `"${v || ""}"`).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = "applications.csv"; a.click();
}

export default function AgencyDashboard({ agency, onLogout }) {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [hoveredRow, setHoveredRow] = useState(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("applications");

  const applyLink = `quikcare.co.uk/apply/${agency.slug}`;

  useEffect(() => {
    const q = query(collection(db, "applications"), where("agencySlug", "==", agency.slug), orderBy("createdAt", "desc"));
    const unsub = onSnapshot(q, (snap) => {
      setApplications(snap.docs.map(d => normalize({ id: d.id, ...d.data() })));
      setLoading(false);
    }, (err) => {
      console.error("Error loading applications:", err);
      setError(err.message || "Failed to load applications");
      setLoading(false);
    });
    return () => unsub();
  }, [agency.slug]);

  const updateStatus = async (id, status) => {
    try {
      await updateDoc(doc(db, "applications", id), { status });
      // Send email notification if emailjs is available
      if (window.emailjs) {
        const app = applications.find(a => a.id === id);
        if (app) {
          window.emailjs.send('QUIKCARE', 'template_as31vzw', {
            carer_name: `${app.firstName || ''} ${app.lastName || ''}`,
            carer_email: app.email || app.userEmail || '',
            agency_name: agency.agencyName || 'Quikcare',
            status: status === 'approved' ? 'approved' : 'unsuccessful',
            status_message: status === 'approved'
              ? 'Congratulations! Your application has been approved. The agency will be in touch shortly.'
              : 'Thank you for applying. Unfortunately your application was not successful at this time.',
          }, 'LD1-M8qPWz2Go1fM2').catch(e => console.log('Email failed:', e));
        }
      }
    } catch (err) {
      console.log('Update error:', err);
    }
  };

  const deleteApplication = async (id, name) => {
    if (!window.confirm(`Are you sure you want to permanently delete ${name}'s application? This cannot be undone.`)) return;
    try { await deleteDoc(doc(db, "applications", id)); } catch (err) { console.error("Delete error:", err); }
  };

  const stats = {
    total: applications.length,
    pending: applications.filter(a => a.status === "pending").length,
    approved: applications.filter(a => a.status === "approved").length,
    rejected: applications.filter(a => a.status === "rejected").length,
  };

  const filtered = useMemo(() => {
    return applications.filter(a => {
      const matchFilter = filter === "all" || a.status === filter;
      const q = search.toLowerCase();
      const matchSearch = !q || `${a.firstName} ${a.lastName} ${a.email} ${a.postcode}`.toLowerCase().includes(q);
      return matchFilter && matchSearch;
    });
  }, [applications, filter, search]);

  const copyLink = () => {
    navigator.clipboard.writeText(`https://${applyLink}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div style={s.app}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Serif+Display&family=DM+Sans:wght@300;400;500;600&display=swap" rel="stylesheet" />
      <div style={s.header}>
        <div style={s.logo}>
          <div style={s.logoIcon}>Q</div>
          <span style={s.logoText}>Quikcare</span>
        </div>
        <div style={s.headerRight}>
          <span style={s.agencyBadge}>{agency.agencyName}</span>
          <button style={s.trainingBtn} onClick={() => { window.history.pushState({}, '', '/training'); window.dispatchEvent(new PopStateEvent('popstate', { state: {} })); }}>📋 Training Matrix</button>
          <button style={s.logoutBtn} onClick={onLogout}>Sign Out</button>
        </div>
      </div>

      {/* Tab Bar */}
      <div style={s.tabRow}>
        <button style={s.tab(activeTab === "applications")} onClick={() => setActiveTab("applications")}>📁 Applications</button>
        <button style={s.tab(activeTab === "references")} onClick={() => setActiveTab("references")}>⭐ References</button>
        <button style={s.tab(activeTab === "compliance")} onClick={() => setActiveTab("compliance")}>✅ Compliance</button>
        <button style={s.tab(activeTab === "dbs")} onClick={() => setActiveTab("dbs")}>🔒 DBS</button>
      </div>

      {activeTab === "references" && <ReferencesPanel agency={agency} applications={applications} />}
      {activeTab === "compliance" && <CompliancePanel agency={agency} applications={applications} />}
      {activeTab === "dbs" && <DBSPanel agency={agency} />}

      {activeTab === "applications" && <>
      {error && (
        <div style={{ background: "#fff0f0", border: "1px solid #ffb3b3", borderRadius: 10, margin: "24px auto", maxWidth: 1100, padding: "16px 20px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <div style={{ color: "#cc0000", fontWeight: 700, fontSize: 14, marginBottom: 4 }}>⚠️ Something went wrong</div>
            <div style={{ color: "#cc0000", fontSize: 13 }}>{error}</div>
            {error.includes("index") && (
              <div style={{ color: "#9b7fd4", fontSize: 12, marginTop: 6 }}>
                💡 A Firestore index may still be building — wait 1–2 minutes and refresh.
              </div>
            )}
          </div>
          <button onClick={() => { setError(null); setLoading(true); }} style={{ padding: "8px 14px", borderRadius: 8, border: "1px solid #ffb3b3", background: "white", color: "#cc0000", fontSize: 13, cursor: "pointer" }}>
            Retry
          </button>
        </div>
      )}

      <div style={s.container}>
        {/* Share Your Link Section */}
        <div style={{ background: "#ffffff", border: "1px solid #e8e0f5", borderRadius: 12, padding: "20px", marginBottom: 20 }}>
          <div style={{ fontSize: 11, color: "#9b7fd4", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 12 }}>📤 Share Your Application Link</div>

          <div style={{ background: "#f8f5ff", border: "1px solid #e8e0f5", borderRadius: 8, padding: "12px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
            <div style={{ color: "#6C3FC5", fontSize: 14, fontWeight: 600 }}>🔗 https://{applyLink}</div>
            <button style={s.copyBtn} onClick={copyLink}>{copied ? "✓ Copied!" : "Copy Link"}</button>
          </div>

          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
            <a href={`https://wa.me/?text=${encodeURIComponent("Hi! We're hiring care workers. Apply to join our team here: https://" + applyLink)}`}
              target="_blank" rel="noreferrer"
              style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 14px", background: "#25D366", borderRadius: 8, color: "white", fontSize: 13, fontWeight: 600, textDecoration: "none" }}>
              📱 WhatsApp
            </a>
            <a href={`mailto:?subject=Apply to join our care team&body=Hi,%0D%0A%0D%0AWe are currently recruiting care workers. Please apply using the link below:%0D%0A%0D%0Ahttps://${applyLink}%0D%0A%0D%0AKind regards,%0D%0A${agency.agencyName}`}
              style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 14px", background: "#6C3FC5", borderRadius: 8, color: "white", fontSize: 13, fontWeight: 600, textDecoration: "none" }}>
              📧 Email
            </a>
            <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent("https://" + applyLink)}`}
              target="_blank" rel="noreferrer"
              style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 14px", background: "#1877F2", borderRadius: 8, color: "white", fontSize: 13, fontWeight: 600, textDecoration: "none" }}>
              📘 Facebook
            </a>
            <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent("https://" + applyLink)}`}
              target="_blank" rel="noreferrer"
              style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 14px", background: "#0A66C2", borderRadius: 8, color: "white", fontSize: 13, fontWeight: 600, textDecoration: "none" }}>
              💼 LinkedIn
            </a>
          </div>

        </div>

        <div style={s.statsRow}>
          <div style={s.statCard("#6C3FC5")}><div style={s.statNum("#6C3FC5")}>{stats.total}</div><div style={s.statLabel}>Total Applications</div></div>
          <div style={s.statCard("#f59e0b")}><div style={s.statNum("#f59e0b")}>{stats.pending}</div><div style={s.statLabel}>Pending Review</div></div>
          <div style={s.statCard("#1a7a3a")}><div style={s.statNum("#1a7a3a")}>{stats.approved}</div><div style={s.statLabel}>Approved</div></div>
          <div style={s.statCard("#cc0000")}><div style={s.statNum("#cc0000")}>{stats.rejected}</div><div style={s.statLabel}>Rejected</div></div>
        </div>

        <div style={s.toolbar}>
          <input style={s.searchInput} placeholder="🔍  Search by name, email or postcode..." value={search} onChange={e => setSearch(e.target.value)} />
          {["all", "pending", "approved", "rejected"].map(f => (
            <button key={f} style={s.filterBtn(filter === f)} onClick={() => setFilter(f)}>
              {f === "all" ? "All" : STATUS_LABELS[f]}
            </button>
          ))}
          <button style={s.exportBtn} onClick={() => exportCSV(filtered)}>⬇ Export CSV</button>
        </div>

        <div style={{ background: "#ffffff", border: "1px solid #e8e0f5", borderRadius: 12, overflow: "hidden" }}>
          {loading ? <div style={s.loading}>Loading applications...</div> : (
            <table style={s.table}>
              <thead>
                <tr style={{ background: "#f8f5ff" }}>
                  {["Applicant", "Location", "Experience", "DBS", "Applied", "Status"].map(h => <th key={h} style={s.th}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={6} style={s.empty}>
                    {applications.length === 0 ? `No applications yet — share your link: https://${applyLink}` : "No applications match your filter"}
                  </td></tr>
                ) : filtered.map(app => (
                  <tr key={app.id} style={{ background: hoveredRow === app.id ? "#f8f5ff" : "transparent", cursor: "pointer", transition: "background 0.15s" }}
                    onClick={() => setSelected(app)} onMouseEnter={() => setHoveredRow(app.id)} onMouseLeave={() => setHoveredRow(null)}>
                    <td style={s.td}><div style={{ fontWeight: 600 }}>{app.firstName} {app.lastName}</div><div style={s.tdSub}>{app.email}</div></td>
                    <td style={s.td}><div>{app.postcode}</div><div style={s.tdSub}>{app.driving === "Yes" ? "🚗 Driver" : "No car"}</div></td>
                    <td style={s.td}><div>{app.years}</div><div style={s.tdSub}>{app.quals?.[0]}</div></td>
                    <td style={s.td}><div>{app.hasDbs}</div><div style={s.tdSub}>{app.updateService === "Yes" ? "Update Service ✓" : ""}</div></td>
                    <td style={s.td}>{app.appliedAt}</td>
                    <td style={s.td}><span style={s.statusPill(app.status)}>{STATUS_LABELS[app.status]}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div style={{ marginTop: 10, fontSize: 12, color: "#9b7fd4" }}>
          {!loading && `Showing ${filtered.length} of ${applications.length} applications · Updates in real time`}
        </div>
      </div>

      <Modal app={selected} agency={agency} onClose={() => setSelected(null)} onApprove={(id) => updateStatus(id, "approved")} onReject={(id) => updateStatus(id, "rejected")} onDelete={deleteApplication} />
      </>}
    </div>
  );
}
